/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  EuiAccordion,
  EuiBadge,
  EuiFieldText,
  EuiFormRow,
  EuiText,
} from "@inscopix/ideas-eui";
import { HelpIconTooltip } from "components/HelpIconTooltip/HelpIconTooltip";
import pDebounce from "p-debounce";
import { useDataTableContext } from "pages/gdt/store/DataTableProvider";
import { useEffect, useState } from "react";

/**
 * Generates a unique table key by appending a number to the default key if it's
 * is already in use.
 * @param tableName
 * @param usedKeys
 * @returns The unique key.
 */
export const generateUniqueTableKey = pDebounce(
  (tableName: string, usedKeys: Set<string>) => {
    const defaultKey = tableName
      .toUpperCase()
      .replace(TABLE_KEY_ILLEGAL_CHAR_REGEX, "")
      .slice(0, 3);

    if (usedKeys.has(defaultKey)) {
      let suffix = 2;
      let uniqueKey = `${defaultKey}${suffix}`;
      while (usedKeys.has(uniqueKey)) {
        suffix++;
        uniqueKey = `${defaultKey}${suffix}`;
      }
      return uniqueKey;
    } else {
      return defaultKey;
    }
  },
  500,
);

// Field validation constants
const TABLE_KEY_ILLEGAL_CHAR_REGEX = /[^A-Z0-9_]/g;
const TABLE_KEY_MAX_LENGTH = 10;

interface FieldTableKeyProps {
  autoFocus?: boolean;
  label?: string;
  value: string;
  onChange: (field: { value: string; isValid: boolean }) => void;
  expandRequirements?: boolean;
  tableName: string;
  autoGenerate?: boolean;
  onAutoGenerate?: (key: string) => void;
}

export const FieldTableKey = ({
  autoFocus,
  label = "Key",
  value,
  onChange,
  expandRequirements = true,
  tableName,
  autoGenerate = false,
  onAutoGenerate,
}: FieldTableKeyProps) => {
  const usedKeys = useDataTableContext(
    (s) => new Set(s.tables.map((t) => t.key)),
    () => true,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [initialValue] = useState(value);
  const [initialOnAutoGenerate] = useState(() => onAutoGenerate);

  const isKeyUnique = (key: string) => {
    return initialValue === key || !usedKeys.has(key);
  };

  useEffect(() => {
    if (autoGenerate) {
      setIsLoading(true);
      void generateUniqueTableKey(tableName, usedKeys)
        .then((key) => initialOnAutoGenerate?.(key))
        .finally(() => setIsLoading(false));
    }
  }, [autoGenerate, initialOnAutoGenerate, tableName, usedKeys]);

  return (
    <EuiFormRow
      fullWidth
      label={label}
      helpText={
        expandRequirements ? (
          <EuiAccordion
            id="key_reqs"
            initialIsOpen={expandRequirements}
            isDisabled={false}
            buttonContent={
              <EuiText size="xs" style={{ cursor: "pointer" }}>
                Requirements
              </EuiText>
            }
          >
            <EuiText size={"s"}>
              <p>
                This key combines with a row&apos;s sequential number to create
                a unique ID for each row in the table.
              </p>
              <ul>
                <li>Maximum 10 characters.</li>
                <li>Uppercase letters, numbers, and underscores.</li>
                <li>Keys must be unique within this project.</li>
                <li>Can be edited after initial save.</li>
              </ul>
            </EuiText>
          </EuiAccordion>
        ) : null
      }
      isInvalid={!isKeyUnique(value)}
      error={
        !isKeyUnique(value)
          ? `Your key must be unique to this project - ${value} is already in use.`
          : undefined
      }
      css={css`
        .euiFormControlLayout > svg {
          margin: 0px 5px;
        }
      `}
    >
      <EuiFieldText
        isLoading={isLoading}
        disabled={isLoading}
        fullWidth
        placeholder="eg. REC"
        name="prefix"
        append={
          expandRequirements ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 5,
                paddingRight: 10,
              }}
            >
              <EuiText size="s">Example: </EuiText>
              <EuiBadge
                style={{
                  fontFamily: "monospace",
                  border: "1px solid grey",
                }}
              >
                {value === "" ? "REC" : value}-1
              </EuiBadge>
            </div>
          ) : (
            <HelpIconTooltip
              content={
                <EuiText size={"s"}>
                  <p>
                    This key combines with a row&apos;s sequential number to
                    create a unique ID for each row in the table.
                  </p>
                  <ul>
                    <li>Maximum 10 characters.</li>
                    <li>Uppercase letters, numbers, and underscores.</li>
                    <li>Keys must be unique within this project.</li>
                    <li>Can be edited after initial save.</li>
                  </ul>
                </EuiText>
              }
            />
          )
        }
        value={value}
        onChange={(e) => {
          const newKey = e.target.value
            .toUpperCase()
            .replace(TABLE_KEY_ILLEGAL_CHAR_REGEX, "")
            .substring(0, TABLE_KEY_MAX_LENGTH);

          onChange({
            value: newKey,
            isValid: isKeyUnique(newKey),
          });
        }}
        maxLength={TABLE_KEY_MAX_LENGTH}
        isInvalid={false}
        autoFocus={autoFocus}
      />
    </EuiFormRow>
  );
};
